// Mail V2 Demo data — 純前端、純靜態。
// 不得接後端、不得接 IMAP/SMTP、不得接 AI。
// 刷新頁面 = 恢復默認狀態。

export type MailAccountType = "personal" | "shared" | "other";

export type MailAccount = {
  id: string;
  email: string;
  displayName: string;
  accountType: MailAccountType;
  unreadCount: number;
  /** Mail V2.5 (2026-08-20): アカウント レベル 同期 状態 (locked plan §16.9 §D).
   *  undefined / 'ok' = 正常; 'error' = 直近 sync 失敗 → red banner + 修復 ボタン */
  lastSyncStatus?: "ok" | "error";
  lastSyncError?: string | null;
  lastSyncErrorAt?: string | null;
};

export type MailFolder = {
  id: string;
  name: string;
  iconName:
    | "Inbox"
    | "Star"
    | "Send"
    | "FileText"
    | "AlertOctagon"
    | "Trash2"
    | "Archive";
  count?: number;
};

export type MailLabel = {
  id: string;
  name: string;
  color: string; // CSS color (hex / rgba / named)
  count?: number;
};

export type MailStatus = "unhandled" | "handling" | "handled";

export type MailMessage = {
  id: string;
  accountId: string;
  folderId: string;
  senderName: string;
  senderEmail: string;
  recipients: string[];
  subject: string;
  preview: string;
  receivedAt: string; // ISO 8601 UTC
  isRead: boolean;
  isStarred: boolean;
  status: MailStatus;
  labels: string[];
  htmlBody: string;
};

export type CustomerContext = {
  companyName: string;
  contactName: string;
  lastContact: string;
  relatedProject: string;
  chatbotCount: number;
  crmStatus: string;
};

// ============================================================
// 賬號
// ============================================================

export const DEMO_ACCOUNTS: MailAccount[] = [
  {
    id: "acc-wang-personal",
    email: "wang@company.com",
    displayName: "Wang",
    accountType: "personal",
    unreadCount: 119,
  },
  {
    id: "acc-wang-private",
    email: "wang.private@gmail.com",
    displayName: "Wang (個人)",
    accountType: "personal",
    unreadCount: 23,
  },
  {
    id: "acc-support",
    email: "support@company.com",
    displayName: "サポート",
    accountType: "shared",
    unreadCount: 8,
  },
  {
    id: "acc-sales",
    email: "sales@company.com",
    displayName: "セールス",
    accountType: "shared",
    unreadCount: 14,
  },
  {
    id: "acc-info",
    email: "info@company.com",
    displayName: "インフォ",
    accountType: "shared",
    unreadCount: 0,
  },
  {
    id: "acc-admin",
    email: "admin@company.com",
    displayName: "管理者",
    accountType: "other",
    unreadCount: 0,
  },
];

// ============================================================
// 目錄
// ============================================================

export const DEMO_FOLDERS: MailFolder[] = [
  { id: "inbox", name: "受信トレイ", iconName: "Inbox", count: 119 },
  { id: "starred", name: "スター付き", iconName: "Star" },
  { id: "sent", name: "送信済み", iconName: "Send" },
  { id: "drafts", name: "下書き", iconName: "FileText", count: 5 },
  { id: "spam", name: "迷惑メール", iconName: "AlertOctagon", count: 8 },
  { id: "trash", name: "ゴミ箱", iconName: "Trash2" },
  { id: "all", name: "すべてのメール", iconName: "Archive" },
];

// ============================================================
// 標籤
// ============================================================

export const DEMO_LABELS: MailLabel[] = [
  { id: "lbl-customer", name: "顧客対応", color: "#2563eb", count: 24 },
  { id: "lbl-google-ads", name: "Google Ads", color: "#f59e0b", count: 36 },
  { id: "lbl-project", name: "プロジェクト関連", color: "#10b981", count: 18 },
  { id: "lbl-finance", name: "経理", color: "#a855f7", count: 8 },
  { id: "lbl-vip", name: "重要顧客", color: "#ef4444", count: 12 },
];

// ============================================================
// 客戶上下文
// ============================================================

export const DEMO_CUSTOMER_CONTEXT: CustomerContext = {
  companyName: "Google Japan",
  contactName: "田中 一郎",
  lastContact: "昨日",
  relatedProject: "Google Ads 最適化",
  chatbotCount: 12,
  crmStatus: "進行中",
};

// ============================================================
// 郵件正文模板 (HTML — 不含 script/style,純展示用)
// ============================================================

const GOOGLE_ADS_BODY = `
<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1f2937;">
  <div style="padding: 24px 0; border-bottom: 1px solid #e5e7eb;">
    <img src="https://www.gstatic.com/images/branding/googlelogo/2x/googlelogo_color_112x36dp.png" alt="Google Ads" width="112" style="display:block;">
  </div>
  <h2 style="font-size: 18px; margin: 24px 0 12px;">アカウント停止に関する重要なご連絡</h2>
  <p>Google Ads アカウント <strong>(575-388-8780)</strong> が、当社の広告ポリシーに違反する行為が確認されたため、停止されました。</p>
  <p>アカウントを再開するには、以下の手順をご確認ください:</p>
  <ol style="padding-left: 20px; line-height: 1.8;">
    <li>アカウントのポリシー違反内容をご確認ください</li>
    <li>必要に応じて修正を実施してください</li>
    <li>異議申し立てを行う場合は、再審査リクエストをご提出ください</li>
  </ol>
  <a href="#" style="display:inline-block; margin-top:16px; padding:10px 20px; background:#1a73e8; color:#fff; text-decoration:none; border-radius:6px; font-weight:500;">アカウントを確認</a>
  <p style="margin-top: 24px; color: #6b7280; font-size: 13px;">
    ※ このメールは自動送信されています。<br>
    Google Ads チーム
  </p>
</div>
`;

const PLAYSTATION_BODY = `
<div style="font-family: -apple-system, BlinkMacSystemFont, sans-serif; color: #1f2937; background:#ffffff;">
  <div style="background: linear-gradient(135deg, #003791, #0070d1); padding: 32px 24px; text-align: center;">
    <div style="color: #fff; font-size: 28px; font-weight: 700; letter-spacing: 2px;">PlayStation</div>
  </div>
  <div style="padding: 32px 24px;">
    <h1 style="font-size: 22px; margin-bottom: 16px;">謹賀新年</h1>
    <p style="line-height: 1.8;">旧年中は大変お世話になりまして、誠にありがとうございました。</p>
    <p style="line-height: 1.8;">本年も PlayStation サービスをどうぞよろしくお願いいたします。</p>
    <p style="margin-top: 24px; font-size: 14px; color: #6b7280;">PlayStation チーム一同</p>
  </div>
</div>
`;

const ADOBE_BODY = `
<div style="font-family: 'Helvetica Neue', sans-serif; color: #1f2937;">
  <div style="background:#fa0f00; padding:24px; text-align:center;">
    <strong style="color:#fff; font-size: 24px;">Adobe</strong>
  </div>
  <div style="padding: 32px 24px;">
    <h2 style="font-size: 20px;">5 分でわかる! Adobe Firefly の安全設計</h2>
    <p>Adobe Firefly は、商用利用可能な生成 AI として設計されました。</p>
    <table cellpadding="12" style="margin: 16px 0; width: 100%; border-collapse: collapse;">
      <tr style="background: #f9fafb;"><td><strong>学習データ</strong></td><td>Adobe Stock + ライセンス許諾済みコンテンツのみ</td></tr>
      <tr><td><strong>著作権保護</strong></td><td>Content Credentials を自動付与</td></tr>
      <tr style="background: #f9fafb;"><td><strong>商用利用</strong></td><td>可能(プランによる)</td></tr>
    </table>
    <a href="#" style="display:inline-block; padding:10px 20px; background:#fa0f00; color:#fff; text-decoration:none; border-radius:6px;">詳細を見る</a>
  </div>
</div>
`;

const EA_BODY = `
<div style="font-family: Arial, sans-serif; color: #1f2937;">
  <div style="background: #1f2937; padding: 24px; text-align: center;">
    <strong style="color: #facc15; font-size: 24px;">EA</strong>
  </div>
  <div style="padding: 32px 24px;">
    <h2 style="font-size: 18px;">EA アカウントへようこそ</h2>
    <p>EA アカウントの登録が完了しました。</p>
    <p style="color: #6b7280;">アカウント ID: EA-12345-ABCDE</p>
    <p style="margin-top: 16px;"><strong>セキュリティコード: 334460</strong></p>
    <p style="color: #ef4444; font-size: 13px;">※ このコードは 10 分有効です。誰にも共有しないでください。</p>
  </div>
</div>
`;

const SCORECLOUD_BODY = `
<div style="font-family: 'Segoe UI', sans-serif; color: #1f2937;">
  <h2 style="font-size: 18px;">Major Update — Audio Playback in Your Score 🎙️🎼</h2>
  <p>ScoreCloud Team は本日、Audio Playback 機能をリリースしました。</p>
  <p>譜面を読みながら音声を聴き、どこが改善できるかを確認できます。</p>
  <a href="#" style="display:inline-block; margin-top:12px; padding:8px 16px; background:#4f46e5; color:#fff; text-decoration:none; border-radius:6px;">今すぐ試す</a>
</div>
`;

const GOOGLE_PLAY_BODY = `
<div style="font-family: -apple-system, BlinkMacSystemFont, sans-serif; color: #1f2937;">
  <h2 style="font-size: 18px;">Google Play 隐私设置运作方式更新</h2>
  <p>为了向您提供更优质的服务,我们更新了 Google Play 的隐私设置页面。新版本将清晰地展示您的数据如何被收集、使用和保护。</p>
  <p style="color: #6b7280; font-size: 13px;">如有疑问,请访问我们的帮助中心。</p>
</div>
`;

const GOOGLE_AD_BODY = `
<div style="font-family: -apple-system, BlinkMacSystemFont, sans-serif; color: #1f2937;">
  <h2 style="font-size: 18px;">Your campaigns are performing well</h2>
  <p>Hi Wang,</p>
  <p>Your Google Ads campaigns generated <strong>1,284 clicks</strong> and <strong>32 conversions</strong> last week, a 12% increase from the previous period.</p>
  <ul style="line-height: 1.8;">
    <li>Top campaign: Brand Awareness Q4</li>
    <li>Best performing keyword: portal ポータル</li>
    <li>Recommended action: Increase budget by 15%</li>
  </ul>
  <a href="#" style="display:inline-block; margin-top:12px; padding:10px 20px; background:#1a73e8; color:#fff; text-decoration:none; border-radius:6px;">View report</a>
</div>
`;

const X_NOTIFICATION_BODY = `
<div style="font-family: -apple-system, BlinkMacSystemFont, sans-serif; color: #1f2937;">
  <h2 style="font-size: 18px;">@GalaxyOcean_pjt, check out the notifications you have on X</h2>
  <p>You've got 3 recent notifications you haven't seen yet.</p>
  <p style="margin-top: 16px;"><strong>Take a look:</strong></p>
  <ul style="line-height: 1.8;">
    <li>2 new mentions from @TechPortal</li>
    <li>1 new follower</li>
  </ul>
</div>
`;

const JP_CUSTOMER_INQUIRY_BODY = `
<div style="font-family: 'Hiragino Sans', 'Yu Gothic', sans-serif; color: #1f2937; line-height: 1.8;">
  <p>ポータル商事<br>王 様</p>
  <p>いつもお世話になっております。<br>株式会社山田製作所の山田でございます。</p>
  <p>このたび、貴社の製品ポータルについてお問い合わせがあり、メールいたしました。</p>
  <p>具体的には、以下の点についてご教示いただけますでしょうか:</p>
  <ol style="padding-left: 20px;">
    <li>年間契約プランの料金体系</li>
    <li>API 連携の可否と仕様</li>
    <li>サポート対応時間</li>
  </ol>
  <p>ご多忙のところ恐縮ですが、ご返信いただけますと幸いです。</p>
  <p style="margin-top: 16px;">何卒よろしくお願いいたします。</p>
  <p>株式会社山田製作所<br>山田 太郎<br>yamada@yamada-mfg.co.jp</p>
</div>
`;

const EN_BUSINESS_BODY = `
<div style="font-family: Arial, sans-serif; color: #1f2937; line-height: 1.7;">
  <p>Dear Mr. Wang,</p>
  <p>I hope this email finds you well.</p>
  <p>Following our meeting at the Tokyo Business Summit last week, I would like to follow up on the potential partnership between our companies regarding the APAC market expansion.</p>
  <p>As discussed, I have attached our preliminary proposal for your review. We believe this collaboration could bring significant value to both parties.</p>
  <p>I would appreciate it if we could schedule a call at your earliest convenience to discuss the next steps.</p>
  <p style="margin-top: 16px;">Best regards,<br>Sarah Johnson<br>Director of Business Development<br>Global Tech Partners<br>sarah.johnson@globaltechpartners.com</p>
</div>
`;

const CN_NOTIFICATION_BODY = `
<div style="font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif; color: #1f2937; line-height: 1.8;">
  <h2 style="font-size: 18px;">您的 Google Ads 账号已被撤销</h2>
  <p>尊敬的用户:</p>
  <p>您的 Google Ads 账号 (575-388-8780) 因违反广告政策,已被系统撤销。</p>
  <p>如有异议,您可以在 30 天内提交申诉。</p>
  <p style="margin-top: 16px; color: #6b7280; font-size: 13px;">Google Ads 中国团队</p>
</div>
`;

const SCORECLOUD_NEW_BODY = `
<div style="font-family: 'Segoe UI', sans-serif; color: #1f2937;">
  <h2 style="font-size: 18px;">Final hours: up to 40% off</h2>
  <p>The ScoreCloud Studio spring sale ends in a few hours.</p>
  <p>Upgrade to Pro and unlock unlimited transcriptions, premium instruments, and priority support.</p>
  <a href="#" style="display:inline-block; margin-top:12px; padding:10px 20px; background:#4f46e5; color:#fff; text-decoration:none; border-radius:6px;">Upgrade now</a>
</div>
`;

const ACME_INVOICE_BODY = `
<div style="font-family: Arial, sans-serif; color: #1f2937; line-height: 1.7;">
  <h2 style="font-size: 18px;">Invoice #INV-2026-0418</h2>
  <p>Attached please find the invoice for the services rendered in March 2026.</p>
  <table cellpadding="8" style="margin: 16px 0; border-collapse: collapse; width: 100%;">
    <tr style="background: #f3f4f6;"><th style="text-align:left;">項目</th><th style="text-align:right;">金額</th></tr>
    <tr><td>コンサルティング料</td><td style="text-align:right;">¥ 320,000</td></tr>
    <tr><td>ライセンス料</td><td style="text-align:right;">¥ 180,000</td></tr>
    <tr style="background: #fef3c7;"><td><strong>合計</strong></td><td style="text-align:right;"><strong>¥ 500,000</strong></td></tr>
  </table>
  <p>お支払い期限: 2026 年 5 月末日</p>
</div>
`;

// ============================================================
// 郵件列表 — 12 封
// ============================================================

export const DEMO_MESSAGES: MailMessage[] = [
  {
    id: "msg-1",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "Google Ads",
    senderEmail: "ads-account-noreply@google.com",
    recipients: ["wang@company.com"],
    subject: "您的 Google Ads 账号 (575-388-8780) 已被撤销",
    preview: "您的 Google Ads 账号因违反广告政策,已被系统撤销。如有异议,您可以在 30 天内提交申诉。",
    receivedAt: "2026-04-01T08:44:11+00:00",
    isRead: false,
    isStarred: true,
    status: "unhandled",
    labels: ["lbl-google-ads", "lbl-vip"],
    htmlBody: GOOGLE_ADS_BODY,
  },
  {
    id: "msg-2",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "PlayStation",
    senderEmail: "email@email.playstation.com",
    recipients: ["wang@company.com"],
    subject: "PlayStationからの新年のご挨拶",
    preview: "謹賀新年。本年も PlayStation サービスをどうぞよろしくお願いいたします。",
    receivedAt: "2026-01-01T02:08:12+00:00",
    isRead: false,
    isStarred: false,
    status: "handling",
    labels: ["lbl-project"],
    htmlBody: PLAYSTATION_BODY,
  },
  {
    id: "msg-3",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "Adobe Creative Cloud",
    senderEmail: "mail@mail.adobe.com",
    recipients: ["wang@company.com"],
    subject: "5分でわかる! Adobe Fireflyの安全設計",
    preview: "Adobe Firefly は、商用利用可能な生成 AI として設計されました。",
    receivedAt: "2025-12-22T16:10:59+00:00",
    isRead: true,
    isStarred: false,
    status: "unhandled",
    labels: ["lbl-project"],
    htmlBody: ADOBE_BODY,
  },
  {
    id: "msg-4",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "EA Account",
    senderEmail: "noreply@ea.com",
    recipients: ["wang@company.com"],
    subject: "お客様のEAセキュリティコード: 334460",
    preview: "セキュリティコードは 10 分有効です。誰にも共有しないでください。",
    receivedAt: "2024-10-31T03:31:27+00:00",
    isRead: true,
    isStarred: false,
    status: "handled",
    labels: [],
    htmlBody: EA_BODY,
  },
  {
    id: "msg-5",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "ScoreCloud Team",
    senderEmail: "team@scorecloud.com",
    recipients: ["wang@company.com"],
    subject: "Major Update — Audio Playback in Your Score 🎙️🎼",
    preview: "譜面を読みながら音声を聴き、どこが改善できるかを確認できます。",
    receivedAt: "2025-12-11T10:00:58+00:00",
    isRead: false,
    isStarred: true,
    status: "unhandled",
    labels: ["lbl-project"],
    htmlBody: SCORECLOUD_BODY,
  },
  {
    id: "msg-6",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "Google Play",
    senderEmail: "play-noreply@google.com",
    recipients: ["wang@company.com"],
    subject: "Google Play 隐私设置运作方式更新",
    preview: "为了向您提供更优质的服务,我们更新了 Google Play 的隐私设置页面。",
    receivedAt: "2026-01-14T01:54:45+00:00",
    isRead: false,
    isStarred: false,
    status: "unhandled",
    labels: [],
    htmlBody: GOOGLE_PLAY_BODY,
  },
  {
    id: "msg-7",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "Google Ads",
    senderEmail: "ads-noreply@google.com",
    recipients: ["wang@company.com"],
    subject: "Your campaigns are performing well",
    preview: "Your Google Ads campaigns generated 1,284 clicks and 32 conversions last week.",
    receivedAt: "2026-03-15T22:30:00+00:00",
    isRead: true,
    isStarred: false,
    status: "handling",
    labels: ["lbl-google-ads"],
    htmlBody: GOOGLE_AD_BODY,
  },
  {
    id: "msg-8",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "X (formerly Twitter)",
    senderEmail: "notify@twitter.com",
    recipients: ["wang@company.com"],
    subject: "@GalaxyOcean_pjt, check out the notifications you have on X",
    preview: "You've got 3 recent notifications you haven't seen yet.",
    receivedAt: "2024-07-31T21:54:48+00:00",
    isRead: true,
    isStarred: false,
    status: "handled",
    labels: [],
    htmlBody: X_NOTIFICATION_BODY,
  },
  {
    id: "msg-9",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "山田 太郎",
    senderEmail: "yamada@yamada-mfg.co.jp",
    recipients: ["wang@company.com"],
    subject: "【お問い合わせ】貴社製品についてご質問",
    preview: "貴社の製品ポータルについてお問い合わせがあり、メールいたしました。",
    receivedAt: "2026-03-20T01:15:00+00:00",
    isRead: false,
    isStarred: true,
    status: "unhandled",
    labels: ["lbl-customer", "lbl-vip"],
    htmlBody: JP_CUSTOMER_INQUIRY_BODY,
  },
  {
    id: "msg-10",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "Sarah Johnson",
    senderEmail: "sarah.johnson@globaltechpartners.com",
    recipients: ["wang@company.com"],
    subject: "Follow-up: APAC Partnership Proposal",
    preview: "Following our meeting at the Tokyo Business Summit, I would like to follow up...",
    receivedAt: "2026-03-18T08:30:00+00:00",
    isRead: false,
    isStarred: false,
    status: "handling",
    labels: ["lbl-vip", "lbl-project"],
    htmlBody: EN_BUSINESS_BODY,
  },
  {
    id: "msg-11",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "Google 通知中心",
    senderEmail: "noreply@accounts.google.com",
    recipients: ["wang@company.com"],
    subject: "新しい端末からのログインについて",
    preview: "新しい端末からのログインを検出しました。本人確認を行ってください。",
    receivedAt: "2026-02-28T14:20:00+00:00",
    isRead: true,
    isStarred: false,
    status: "handled",
    labels: [],
    htmlBody: CN_NOTIFICATION_BODY,
  },
  {
    id: "msg-12",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "ScoreCloud Team",
    senderEmail: "team@scorecloud.com",
    recipients: ["wang@company.com"],
    subject: "Final hours: up to 40% off",
    preview: "The ScoreCloud Studio spring sale ends in a few hours.",
    receivedAt: "2026-03-10T06:20:51+00:00",
    isRead: false,
    isStarred: false,
    status: "unhandled",
    labels: ["lbl-project"],
    htmlBody: SCORECLOUD_NEW_BODY,
  },
  {
    id: "msg-13",
    accountId: "acc-wang-personal",
    folderId: "inbox",
    senderName: "山田製作所 経理部",
    senderEmail: "billing@yamada-mfg.co.jp",
    recipients: ["wang@company.com", "finance@company.com"],
    subject: "ご請求書の送付 (INV-2026-0418)",
    preview: "3 月度のご請求書をお送りいたします。お支払い期限: 2026 年 5 月末日。",
    receivedAt: "2026-03-25T09:00:00+00:00",
    isRead: false,
    isStarred: false,
    status: "unhandled",
    labels: ["lbl-finance", "lbl-customer"],
    htmlBody: ACME_INVOICE_BODY,
  },
];

// ============================================================
// AI 流式生成 Demo — 固定台本(不接 AI、不接 SSE)
// ============================================================

export const DEMO_AI_GENERATED_TEXT = `Google Ads チーム御中

いつもお世話になっております。

ご連絡いただきありがとうございます。
アカウント停止に関する内容を確認いたしました。

現在、社内で状況を確認しております。
詳細を確認のうえ、改めてご連絡いたします。

ご不便をおかけいたしますが、何卒よろしくお願いいたします。

株式会社ポータル商事
王 鵬杰`;

export const DEMO_RELATED_MESSAGES = [
  {
    id: "rel-1",
    subject: "Google Ads ポリシー違反について",
    receivedAt: "2026-03-15T22:30:00+00:00",
    senderEmail: "ads-noreply@google.com",
    preview: "Your campaigns are performing well...",
  },
  {
    id: "rel-2",
    subject: "Re: Google Ads 最適化プラン",
    receivedAt: "2026-02-10T03:00:00+00:00",
    senderEmail: "ads-noreply@google.com",
    preview: "広告パフォーマンスが向上しています...",
  },
];